import './ProductCard.scss'

import React, {RefObject, useEffect, useRef, useState} from 'react'
import {useHistory, useLocation} from 'react-router'
import {hashProduct, Meta, useCart} from '../../../../util/cart'
import {useTranslation} from '../../../../util/i18n'
import {Link} from '../../../../util/link'
import {style} from '../../../../util/style'
import {Theme} from '../../../Theme'
import {CardTitle} from '../../../ui/Card'
import {Icon} from '../../../ui/Icon'
import {
	ProductStatusTypes,
	ProductTaxonomyType,
	ProductType
} from '../../pages/WebshopProduct'
import {Price} from '../../ui/Price'
import {ProductImage} from './ProductImage'
import {ProductQuantity} from './ProductQuantity'
import {ProductVariants} from './ProductVariants'

const onWebshop = window.location.host.includes('webshop')

const styles = style('productcard', [
	'header',
	'label',
	'content',
	'shoptools',
	'price',
	'quantity',
	'additem'
])

export const ProductCard: React.FC<ProductType & ProductTaxonomyType> = ({
	variations,
	...props
}) => {
	const {status, preview} = props
	return (
		<div className={styles().mod('large')}>
			<ProductCardHeader status={status} preview={preview} />
			<ProductCardShoptools variations={variations} large={true} {...props} />
			<ProductCardContent title={preview?.title} text={preview?.text} />
		</div>
	)
}

export const ProductCardPreview: React.FC<
	ProductType & ProductTaxonomyType
> = ({variations, featured = false, ...props}) => {
	const {url, title, status, preview, hero} = props

	return (
		<div className={styles()}>
			<ProductCardHeader
				url={url}
				preview={preview}
				status={status}
				featured={featured}
			/>
			<ProductCardContent
				url={url}
				title={preview?.title || hero?.title || title}
				text={preview?.text || hero?.text}
			/>
			<ProductCardShoptools variations={variations} {...props} />
		</div>
	)
}

const ProductCardHeader: React.FC<{
	url?: string
	preview: any
	status: ProductStatusTypes
	featured?: boolean
}> = ({url, preview, status, featured}) => {
	return (
		<div className={styles.header()}>
			<Link to={url} className={styles.header('imglink')}>
				<ProductCardLabel status={status} featured={featured} />
				<ProductImage noHover {...preview} />
			</Link>
		</div>
	)
}

const ProductCardLabel: React.FC<{
	status: ProductStatusTypes
	featured?: boolean
}> = ({status, featured}) => {
	const t = useTranslation('webshop').products.card
	if (featured)
		return <span className={styles.label.mod('featured')}>{t.featured}</span>
	if (!status || !t[status]) return null
	return <span className={styles.label.mod(status)}>{t[status]}</span>
}

const ProductCardContent: React.FC<{
	url?: string
	title?: string
	text?: string
}> = ({url, title, text}) => {
	if (!title && !text) return null
	const strippedText = text && text.replace(/(<([^>]+)>)/gi, '')

	return (
		<div className={styles.content()}>
			{title && (
				<CardTitle
					theme={Theme.H5}
					className={styles.content('title')}
					title={title}
					url={url}
				/>
			)}
			{text && <div className={styles.content('text')}>{strippedText}</div>}
		</div>
	)
}

const ProductCardShoptools: React.FC<
	ProductType & ProductTaxonomyType & {large?: boolean}
> = ({variations, ...props}) => {
	const {
		status,
		path,
		url,
		id,
		quantity_discount: discounts,
		channel,
		info,
		large
	} = props
	const [quantity, setQuantity] = useState(1)
	const [isWrapped, setWrapped] = useState(false)
	const [meta, setMeta] = React.useState<Meta>({})
	const quantityRef = useRef(null)
	const buttonRef = useRef(null)
	const {cart} = useCart()
	useEffect(() => {
		if (quantityRef?.current && buttonRef?.current) {
			setWrapped(
				quantityRef?.current?.offsetTop !== buttonRef?.current?.offsetTop
			)
		}

		// fill in meta from cart
		variations?.forEach(({id: variationsId, options}) => {
			setMeta((prevMeta) => {
				return {...prevMeta, [variationsId]: options[0]?.id}
			})
		})
	}, [])
	const hasDiscount =
		discounts && discounts.length > 0 && discounts[0].quantity !== ''

	const hasVariations = variations?.length > 0

	const digitalProduct = channel === 'product_detail_downloadable'
	const digitalProductInCart =
		digitalProduct && cart.quantityOfVariantsWith(id + '') > 0
	const orderAllowed =
		onWebshop &&
		status !== 'coming_soon' &&
		status !== 'sold_out' &&
		status !== 'temporarily_unavailable'

	// if ((status === 'sold_out' || status === 'coming_soon') && !large && !info)
	// 	return null

	return (
		<div className={styles.shoptools()}>
			<div className={styles.shoptools('row')}>
				<div
					className={styles
						.shoptools('price')
						.mod({nopadding: !orderAllowed})
						.mod({discounts: !!hasDiscount})}
				>
					<ProductCardPrice minimized={true} quantity={quantity} {...props} />
				</div>
				{orderAllowed && (
					<div className={styles.shoptools('cart')}>
						<div
							className={styles.shoptools('cart-quantity')}
							ref={quantityRef}
						>
							<ProductQuantity
								max={digitalProduct ? 1 : 10000}
								disabled={digitalProduct}
								quantity={quantity}
								setQuantity={setQuantity}
								uniqueKey={path}
							/>
						</div>
						{hasVariations && (
							<div className={styles.shoptools('cart-variants')}>
								<ProductVariants
									variations={variations}
									meta={meta}
									onChange={(typeid, optionid) => {
										setMeta({...meta, [typeid]: optionid})
									}}
								/>
							</div>
						)}
						<div
							className={styles
								.shoptools('cart-additem')
								.is({wrapped: isWrapped})}
							ref={buttonRef}
						>
							<ProductCardAddItem
								id={id}
								url={url}
								status={status}
								quantity={quantity}
								meta={meta}
								digitalProductInCart={digitalProductInCart}
							/>
						</div>
					</div>
				)}
			</div>
			{large && info && (
				<div className={styles.shoptools('info')}>
					<Theme.Wysiwyg>{info}</Theme.Wysiwyg>
				</div>
			)}
		</div>
	)
}

const ProductCardPrice: React.FC<
	ProductType & {
		quantity: number
		minimized?: boolean
	}
> = ({quantity, minimized, ...props}) => {
	const {price, quantity_discount: discounts} = props
	const hasDiscounts =
		discounts?.length > 0 && discounts[0].price_per_piece !== ''

	const isFree = price === '0' || !price
	const t = useTranslation('webshop').products.card

	const nextDiscount =
		discounts &&
		minimized &&
		discounts.find((disc, i) => {
			if (!disc.price_per_piece) return false
			if (!discounts[i + 1]) return true
			if (quantity < Number(disc.quantity)) return true
		})

	return (
		<div className={styles.price()}>
			<Price isFree={isFree}>{isFree ? t.free : `€${price}`}</Price>
			{nextDiscount && minimized && (
				<div className={styles.price('discounts')}>
					<p
						className={styles.price('discounts-discount')}
					>{`€${nextDiscount.price_per_piece} ${t.from} ${nextDiscount.quantity} ${t.pieces}`}</p>
				</div>
			)}
			{hasDiscounts && !minimized && (
				<div className={styles.price('discounts')}>
					{discounts.map((dis) => {
						return (
							<p
								key={dis.quantity}
								className={styles.price('discounts-discount')}
							>{`€${dis.price_per_piece} ${t.from} ${dis.quantity} ${t.pieces}`}</p>
						)
					})}
				</div>
			)}
		</div>
	)
}

const ProductCardAddItem: React.FC<{
	id: number
	url: string
	status: ProductStatusTypes
	quantity: number
	meta: Meta
	digitalProductInCart: boolean
}> = ({id, url, status, quantity, meta, digitalProductInCart}) => {
	const [addedProduct, setAddedProduct] = useState(digitalProductInCart)
	const {addProduct} = useCart()

	useEffect(() => {
		if (addedProduct && !digitalProductInCart) {
			const isAdded = setInterval(() => setAddedProduct(false), 1000)
			return () => clearTimeout(isAdded)
		}
	}, [addedProduct])

	const t = useTranslation('webshop').products.card
	const history = useHistory()

	// Don't show add button when not on webshop
	if (!onWebshop) return null

	const hash = '#newsletterproduct'

	switch (status) {
		case 'sold_out':
		case 'temporarily_unavailable':
			return (
				<Theme.Button
					mod={['lightgreybg', 'noicon']}
					onClick={() => history.push({pathname: url, search: null, hash})}
					classPrefix={styles.additem('button')}
					disabled={digitalProductInCart}
				>
					{t.notifiy}
				</Theme.Button>
			)
		case 'coming_soon':
			return null
		case 'available':
		case 'last_pieces':
		default:
			return (
				<Theme.Button
					mod={['lightgreybg', 'noicon']}
					onClick={() => {
						try {
							setAddedProduct(true)
							addProduct({id: id.toString(), meta}, quantity)
						} catch (error) {
							throw new Error(
								`Something went wrong trying to add this item to the cart: ${error}`
							)
						}
					}}
					classPrefix={styles.additem('button')}
					disabled={digitalProductInCart}
				>
					{addedProduct ? (
						<div
							className={styles
								.additem('button-content')
								.is({saving: addedProduct})}
						>
							<span
								className={styles.additem('button-content-item').mod('icon')}
							>
								<Icon icon="checkMark" />
							</span>
							<span className={styles.additem('button-content-item')}>
								{t.added}
							</span>
						</div>
					) : (
						t.add
					)}
				</Theme.Button>
			)
	}
}
